/**
 * Top-level theme + provider tree.
 *
 * The app is light-only by design — no dark-mode toggle, no palette
 * switching. Consumers should prefer primitives from `./components/*`
 * over importing MUI components wholesale — the primitives expose the
 * shared design language (Skeleton, ToastProvider, ConfirmDialog, etc.)
 * and keep markup rewrites minimal.
 */
import React, { useMemo } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { lightPalette, shape, typography } from "./tokens";
import { AppGlobalStyles } from "./GlobalStyles";

export function ThemeProvider({ children }) {
    const theme = useMemo(() => {
        return createTheme({
            palette: lightPalette,
            shape,
            typography,
            spacing: 4,
            components: {
                MuiButtonBase: {
                    defaultProps: { disableRipple: false },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundImage: "none",
                            backgroundColor: "var(--pt-paper)",
                            color: "var(--pt-text-primary)",
                        },
                    },
                },
                MuiAppBar: {
                    styleOverrides: {
                        root: {
                            backgroundImage: "none",
                            backgroundColor: "var(--pt-paper)",
                            color: "var(--pt-text-primary)",
                            borderBottom: "1px solid var(--pt-border-default)",
                        },
                    },
                },
                MuiTooltip: {
                    styleOverrides: {
                        tooltip: {
                            backgroundColor: "#0f172a",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            padding: "6px 10px",
                            borderRadius: 6,
                        },
                        arrow: { color: "#0f172a" },
                    },
                },
                MuiChip: {
                    styleOverrides: {
                        root: {
                            fontWeight: 500,
                            fontSize: "0.75rem",
                        },
                    },
                },
                MuiSnackbarContent: {
                    styleOverrides: {
                        root: { borderRadius: 10 },
                    },
                },
                // Filled Alert inherits MuiPaper's color rule (which is set to
                // --pt-text-primary for the rest of the app), so MUI's per-severity
                // contrastText gets clobbered and we get black-on-green/warning/etc.
                // Pin text color per severity so the alert stays readable on its
                // own background.
                MuiAlert: {
                    styleOverrides: {
                        filled: ({ theme }) => ({
                            color: "#fff",
                            "& .MuiAlert-icon": { color: "#fff" },
                            "& .MuiAlert-action .MuiIconButton-root": {
                                color: "rgba(255,255,255,0.92)",
                                "&:hover": {
                                    color: "#fff",
                                    backgroundColor: "rgba(255,255,255,0.12)",
                                },
                            },
                        }),
                        filledSuccess: ({ theme }) => ({
                            backgroundColor: theme.palette.success.main,
                        }),
                        filledWarning: ({ theme }) => ({
                            backgroundColor: theme.palette.warning.main,
                            // Warning's contrastText in tokens is #0f172a (dark),
                            // so keep dark text + dark close button on amber bg.
                            color: theme.palette.warning.contrastText,
                            "& .MuiAlert-icon": {
                                color: theme.palette.warning.contrastText,
                            },
                            "& .MuiAlert-action .MuiIconButton-root": {
                                color: "rgba(15,23,42,0.7)",
                                "&:hover": {
                                    color: theme.palette.warning.contrastText,
                                    backgroundColor: "rgba(15,23,42,0.08)",
                                },
                            },
                        }),
                        filledError: ({ theme }) => ({
                            backgroundColor: theme.palette.error.main,
                        }),
                        filledInfo: ({ theme }) => ({
                            backgroundColor: theme.palette.info.main,
                        }),
                    },
                },
            },
        });
    }, []);

    return (
        <MuiThemeProvider theme={theme}>
            <CssBaseline />
            <AppGlobalStyles />
            {children}
        </MuiThemeProvider>
    );
}